import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { EmptyState, ErrorState, PageHeader } from '@repo/ui';
import { allNavItems } from '../shell/nav';
import { Page } from '../shell/AppShell';

/** Placeholder for modules scheduled in later phases: real header + breadcrumbs, empty state. */
export function ComingSoonPage({ navKey }: { navKey: string }) {
  const { t } = useTranslation();
  const item = allNavItems.find((i) => i.key === navKey);
  const title = t(`nav.items.${navKey}`);
  return (
    <Page>
      <PageHeader breadcrumbs={item ? [t(`nav.groups.${item.group}`), title] : [title]} title={title} />
      <Paper variant="outlined">
        <EmptyState
          icon="construction"
          title={t('comingSoon.title')}
          description={t('comingSoon.text', { phase: item?.phase ?? '—' })}
        />
      </Paper>
    </Page>
  );
}

export function NotFoundPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <EmptyState
        icon="explore_off"
        title={t('errors.notFoundTitle')}
        description={t('errors.notFoundText')}
        action={{ label: t('errors.goHome'), onClick: () => void navigate({ to: '/' }) }}
      />
    </Box>
  );
}

export function ForbiddenPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <EmptyState
        icon="lock"
        title={t('errors.forbiddenTitle')}
        description={t('errors.forbiddenText')}
        action={{ label: t('errors.goHome'), onClick: () => void navigate({ to: '/' }) }}
      />
    </Box>
  );
}

/** Full-page errors shown before the tenant is known (no router, no theme brand yet). */
export function BootError({
  kind,
  onRetry,
  domain,
}: {
  kind: 'tenant' | 'network';
  onRetry: () => void;
  domain: string;
}) {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
      }}
    >
      {kind === 'tenant' ? (
        <EmptyState
          icon="domain_disabled"
          title={t('errors.tenantNotFoundTitle')}
          description={t('errors.tenantNotFoundText', { domain })}
        />
      ) : (
        <ErrorState
          title={t('errors.loadFailedTitle')}
          description={t('errors.loadFailedText')}
          action={{ label: t('common.retry'), onClick: onRetry }}
        />
      )}
    </Box>
  );
}
